import { Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { useTranslate } from "src/locales";
import ByzMuiButton from "src/components/core-components/mui/data-grid/custom-button";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useCardForm } from "src/hooks/useCardForm";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { useAppSelector } from "src/app/hooks";
import { useSnackbar } from "notistack";
import { useEffect, useState, useRef, useCallback } from "react";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import { fetchDependentCardData } from "src/utils/card/fetchDependentCardData";
import { CardField } from "src/utils/card/cardData";

interface Saps2ScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange: (field: any, value: any) => void;
  patientData: any;
  showAlert?: (message: string, severity: "error" | "success") => void;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function SapsIIScoreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  patientId,
  getComId,
  comId,
  onClose,
  showAlert,
  isSaving,
  extraAccess,
}: Saps2ScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);
  const { enqueueSnackbar } = useSnackbar();
  const {
    formData,
    selectedMCPs,
    handleChange,
    handleMCPChange,
    getMCPParameterDetails,
    mcpCacheLoaded,
    getMCPId,
    cardData,
    getScoreValue,
  } = useCardForm({
    cardType: "SAPS2_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key, value);
      });
    },
  });

  const typedFormData = formData as any;
  const [glasgowScoreValue, setGlasgowScoreValue] = useState<number | null>(null);
  const [glasgowCheckComplete, setGlasgowCheckComplete] = useState<boolean>(false);
  const glasgowFetchedRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    glasgowFetchedRef.current = false;
    setGlasgowCheckComplete(false);
  }, [recordId]);

  const autoSelectGlasgowScoreSelect = useCallback((score: number) => {
    const mcpId = 544; // GLASGOW_SCORE_SELECT mcp_id
    const mcpDetails = getMCPParameterDetails(mcpId);

    let selectedId: string | null = null;

    if (score < 6) {
      selectedId = "1687";
    } else if (score >= 6 && score <= 8) {
      selectedId = "1688";
    } else if (score >= 9 && score <= 10) {
      selectedId = "1689";
    } else if (score >= 11 && score <= 13) {
      selectedId = "1690";
    } else if (score >= 14 && score <= 15) {
      selectedId = "1691";
    }

    if (selectedId) {
      const foundDetail = mcpDetails.find(detail => detail.id.toString() === selectedId);
      if (foundDetail) {
        const selectedMCP: McParameterDetailResponseDto = {
          id: foundDetail.id.toString(),
          mcpId: mcpId,
          name: foundDetail.name,
          note: foundDetail.note,
          value: foundDetail.value,
          orderNumber: foundDetail.orderNumber
        };
        handleMCPChange("GLASGOW_SCORE_SELECT", selectedMCP);
        handleChange("GLASGOW_SCORE_SELECT", selectedId);
        parentHandleChange("GLASGOW_SCORE_SELECT", selectedId);
      }
    }
  }, [getMCPParameterDetails, handleMCPChange, handleChange, parentHandleChange]);

  const applyGlasgowValues = useCallback(
    (glasgowScore: number, glasgowScoreId?: number | null) => {
      setGlasgowScoreValue(glasgowScore);
      handleChange("GLASGOW_SCORE", glasgowScore.toString());
      parentHandleChange("GLASGOW_SCORE", glasgowScore.toString());
      autoSelectGlasgowScoreSelect(glasgowScore);
    },
    [autoSelectGlasgowScoreSelect, handleChange, parentHandleChange]
  );

  useEffect(() => {
    const isEditMode = !!recordId;

    const fetchGlasgow = async () => {
      if (glasgowFetchedRef.current) return;

      if (isEditMode && (!typedFormData.ID || typedFormData.ID === "")) {
        glasgowFetchedRef.current = false;
        return;
      }

      glasgowFetchedRef.current = true;

      if (isEditMode) {
        const existingScore = Number(typedFormData.GLASGOW_SCORE) || 0;
        if (existingScore > 0) {
          applyGlasgowValues(existingScore);
        }
        setGlasgowCheckComplete(true);
        return;
      }

      try {
        const dependency = await fetchDependentCardData({
          cardType: "GLASGOW_SCORE",
          patientId,
          orId: 2571,
        });

        const detail = dependency?.detailRecord;
        if (!detail) {
          if (showAlert && onClose && isMountedRef.current) {
            showAlert(
              t("saps2-score.glasgow-score-required") ||
              "Glasgow skoru hesaplanmadan SAPS II Skoru hesaplanamaz!",
              "error"
            );
            onClose();
          }
          setGlasgowCheckComplete(true);
          return;
        }

        const seeScore = Number(detail.SEE_SCORE) || 0;
        const verbalScore = Number(detail.VERBAL_SCORE) || 0;
        const motorScore = Number(detail.MOTOR_SCORE) || 0;
        const glasgowScore = seeScore + verbalScore + motorScore;
        const glasgowScoreId = detail.ID || null;

        if (glasgowScore >= 1) {
          applyGlasgowValues(glasgowScore, glasgowScoreId);
        } else if (showAlert && onClose && isMountedRef.current) {
          showAlert(
            t("saps2-score.glasgow-score-required") ||
            "Glasgow skoru hesaplanmadan SAPS II Skoru hesaplanamaz!",
            "error"
          );
          onClose();
        }
      } catch (error) {
        console.error("Error fetching Glasgow Score:", error);
        if (showAlert && onClose && isMountedRef.current) {
          showAlert(
            t("saps2-score.glasgow-score-required") ||
            "Glasgow skoru hesaplanmadan SAPS II Skoru hesaplanamaz!",
            "error"
          );
          onClose();
        }
      } finally {
        setGlasgowCheckComplete(true);
      }
    };

    fetchGlasgow();
  }, [
    applyGlasgowValues,
    onClose,
    parentHandleChange,
    patientId,
    recordId,
    showAlert,
    t,
    typedFormData.ID,
  ]);

  useEffect(() => {
    if (glasgowScoreValue !== null && glasgowScoreValue >= 1) {
      autoSelectGlasgowScoreSelect(glasgowScoreValue);
    }
  }, [glasgowScoreValue, autoSelectGlasgowScoreSelect]);

  useEffect(() => {
    if (recordId || !patientData || !mcpCacheLoaded) return;

    try {
      const patientInfo = patientData.data || patientData;
      const patientGender = patientInfo.sexuality;

      if (!patientGender) return;

      const genderMcpId = getMCPId("GENDER");
      if (!genderMcpId) return;

      const genderMcpDetails = getMCPParameterDetails(genderMcpId);


      const genderUpper = String(patientGender).toUpperCase().trim();
      const isFemale = genderUpper === "KADIN" || genderUpper === "K" || genderUpper === "FEMALE" || genderUpper === "F";

      const femaleNames = ["Kadın", "Female", "K", "F"];
      const maleNames = ["Erkek", "Male", "E", "M"];
      const searchNames = isFemale ? femaleNames : maleNames;

      const matchedDetail = genderMcpDetails.find((detail: any) =>
        searchNames.some(name => {
          const detailNameUpper = detail.name?.toUpperCase() || "";
          const searchNameUpper = name.toUpperCase();
          return detailNameUpper.includes(searchNameUpper) || detailNameUpper === searchNameUpper;
        })
      );

      if (matchedDetail && !selectedMCPs["GENDER"]) {
        const selectedMCP: McParameterDetailResponseDto = {
          id: matchedDetail.id.toString(),
          mcpId: genderMcpId,
          name: matchedDetail.name,
          note: matchedDetail.note,
          value: matchedDetail.value,
          orderNumber: matchedDetail.orderNumber
        };
        handleMCPChange("GENDER", selectedMCP);
        handleChange("GENDER", matchedDetail.id.toString());
        parentHandleChange("GENDER", matchedDetail.id.toString());
      }
    } catch (error) {
      console.error("Error setting patient gender:", error);
    }
  }, [patientData, mcpCacheLoaded, recordId, getMCPId, getMCPParameterDetails, handleMCPChange, handleChange, parentHandleChange, selectedMCPs]);

  const handleScoreCalculate = useCallback(() => {
    if (!cardData) return;

    const rowData: any = {
      AGE_XVALUE: getScoreValue("AGE"),
      TYPE_OF_HOSPITALIZATION_XVALUE: getScoreValue("TYPE_OF_HOSPITALIZATION"),
      CHRONIC_DISEASE_XVALUE: getScoreValue("CHRONIC_DISEASE"),
      HEART_RATE_XVALUE: getScoreValue("HEART_RATE"),
      BODY_TEMP_XVALUE: getScoreValue("BODY_TEMP"),
      SERUM_XVALUE: getScoreValue("SERUM"),
      SODIUM_XVALUE: getScoreValue("SODIUM"),
      SYSTOLIC_BLOOD_PRESSURE_XVALUE: getScoreValue("SYSTOLIC_BLOOD_PRESSURE"),
      PAO2_FIO2_XVALUE: getScoreValue("PAO2_FIO2"),
      WBC_XVALUE: getScoreValue("WBC"),
      HCO3_XVALUE: getScoreValue("HCO3"),
      POTASSIUM_XVALUE: getScoreValue("POTASSIUM"),
      BILIRUBIN_XVALUE: getScoreValue("BILIRUBIN"),
      URINE_OUTPUT_XVALUE: getScoreValue("URINE_OUTPUT"),
      GLASGOW_SCORE_SELECT_XVALUE: getScoreValue("GLASGOW_SCORE_SELECT"),
    };

    const saps2ScoreField = cardData.fields.find(
      (f: any) => f.field === "sapsIIScore"
    ) as CardField | undefined;
    if (saps2ScoreField?.calculate) {
      const calculatedScore = saps2ScoreField.calculate(rowData);
      handleChange("SAPSII_SCORE", calculatedScore);
      parentHandleChange("SAPSII_SCORE", calculatedScore);

      // SAPS2 Extended Score hesaplama
      // SAPSII_EXTENDED_SCORE = (0.0742 * SAPSII_SCORE) + (SE_AGE + GENDER + PREVIOUS_LOCATION + DURATION_OF_STAY + CLINICAL_CATEGORY + POISONING)
      const seAgeValue = getScoreValue("SE_AGE") || 0;
      const genderValue = getScoreValue("GENDER") || 0;
      const previousLocationValue = getScoreValue("PREVIOUS_LOCATION") || 0;
      const durationOfStayValue = getScoreValue("DURATION_OF_STAY") || 0;
      const clinicalCategoryValue = getScoreValue("CLINICAL_CATEGORY") || 0;
      const poisoningValue = getScoreValue("POISONING") || 0;

      const extendedScoreSum = seAgeValue + genderValue + previousLocationValue + durationOfStayValue + clinicalCategoryValue + poisoningValue;
      const extendedScore = parseFloat(((calculatedScore * 0.0742) + extendedScoreSum).toFixed(3));

      handleChange("SAPSII_EXTENDED_SCORE", extendedScore);
      parentHandleChange("SAPSII_EXTENDED_SCORE", extendedScore);
    }
  }, [
    cardData,
    getScoreValue,
    handleChange,
    parentHandleChange,
  ]);

  useEffect(() => {
    handleScoreCalculate();
  }, [handleScoreCalculate]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "SAPSII_SCORE" && key !== "SAPSII_EXTENDED_SCORE") {
        parentHandleChange(key, value);
      }
    });
  }, [formData, parentHandleChange]);


  return (
    //AOZMEN;426866;210242025; MADDE ILE SAPS II SKORU FORMU YAPILDI
    <Box sx={{ padding: "20px", width: "100%" }}>
      <Box mb={3}>
        <OtherPrivilegeGuard
          extraAccess={!!extraAccess}
          fallback={
            <ByzMuiSaveButton
              disabled
              tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
            />
          }
        >
          <ByzMuiSaveButton
            onClick={() => save(typedFormData.S_ID || typedFormData.ID || "")}
            disabled={!glasgowCheckComplete || !extraAccess}
            loading={isSaving}
          />
        </OtherPrivilegeGuard>
      </Box>

      <Grid container spacing={2}>
        <Grid
          xs={12}
          sm={6}
          md={6}
          container
          direction={"column"}
          spacing={3}
        >
          {/* TITLE */}
          <Grid
            md={6}
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <ByzMuiTypography isTitle>
              {t("saps2-score.title")}
            </ByzMuiTypography>
          </Grid>

          {/* DATETIME */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("datetime")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMuiDateTimePicker
                sx={{ width: "100%" }}
                value={typedFormData.PROCESS_TIME}
                onChange={(value) => handleChange("PROCESS_TIME", value)}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* CREATED BY */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("createdby")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMuiTextField
                fullWidth
                label={t("createdby")}
                value={typedFormData.CUSER || ""}
                onChange={(e: any) => handleChange("CUSER", e.target.value)}
                disabled
              />
            </Grid>
          </Grid>

          {/* PARAMETERS TITLE */}
          <Grid
            md={6}
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <ByzMuiTypography isTitle>
              {t("saps2-score.parameters")}
            </ByzMuiTypography>
          </Grid>

          {/* AGE */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.age")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("AGE") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("AGE", val);
                  }
                }}
                value={selectedMCPs["AGE"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("AGE") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* LYING POSITION */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.lyingposition")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("TYPE_OF_HOSPITALIZATION") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("TYPE_OF_HOSPITALIZATION", val);
                  }
                }}
                value={selectedMCPs["TYPE_OF_HOSPITALIZATION"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("TYPE_OF_HOSPITALIZATION") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* CHRONIC DISEASE */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.chronicdisease")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("CHRONIC_DISEASE") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("CHRONIC_DISEASE", val);
                  }
                }}
                value={selectedMCPs["CHRONIC_DISEASE"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("CHRONIC_DISEASE") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* HEART RATE */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.heartrate")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("HEART_RATE") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("HEART_RATE", val);
                  }
                }}
                value={selectedMCPs["HEART_RATE"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("HEART_RATE") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* BODY TEMP */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.bodytemp")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("BODY_TEMP") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("BODY_TEMP", val);
                  }
                }}
                value={selectedMCPs["BODY_TEMP"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("BODY_TEMP") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* LAB RESULTS TITLE & BUTTON */}
          <Grid container alignItems="center">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle>
                {t("saps2-score.labresults")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMuiButton
                className="Mui-disabled"
                variant="outlined"
                onClick={() => {
                  enqueueSnackbar("Laboratuvar sonuçları alanı henüz yapım aşamasında", {
                    variant: "info",
                    autoHideDuration: 3000,
                  });
                }}
                color="info"
                size="large"
                sx={{
                  display: "flex",
                  width: "100%",
                  p: 2,
                  backgroundColor: "#63B4FF33",
                }}
              >
                <ByzMuiTypography sx={{ margin: 0 }}>
                  {t("saps2-score.showlabresults")}
                </ByzMuiTypography>
              </ByzMuiButton>
            </Grid>
          </Grid>

          {/* SERORUREORBUM | RESULT */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {t("saps2-score.serorureorbum")}
                <Box sx={{ display: "flex", alignItems: "center", width: "150px" }}>
                  <span style={{ fontSize: "30px", fontWeight: "100", marginRight: "8px" }}>|</span>
                  {t("saps2-score.result")}
                </Box>
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <ByzMcParameterDetailSelect
                  title={t("gbselect")}
                  mcpId={getMCPId("SERUM") || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("SERUM", val);
                    }
                  }}
                  value={selectedMCPs["SERUM"]}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(getMCPId("SERUM") || 0)}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
                <ByzMuiTextField
                  fullWidth
                  placeholder=""
                  disabled
                  value={typedFormData.SERUM_LAB || ""}
                  onChange={(e: any) => handleChange("SERUM_LAB", e.target.value)}
                />
              </Box>
            </Grid>
          </Grid>

          {/* SODIUM | RESULT */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {t("saps2-score.sodium")}
                <Box sx={{ display: "flex", alignItems: "center", width: "150px" }}>
                  <span style={{ fontSize: "30px", fontWeight: "100", marginRight: "8px" }}>|</span>
                  {t("saps2-score.result")}
                </Box>
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <ByzMcParameterDetailSelect
                  title={t("gbselect")}
                  mcpId={getMCPId("SODIUM") || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("SODIUM", val);
                    }
                  }}
                  value={selectedMCPs["SODIUM"]}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(getMCPId("SODIUM") || 0)}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
                <ByzMuiTextField
                  fullWidth
                  placeholder=""
                  disabled
                  value={typedFormData.SODIUM_LAB || ""}
                  onChange={(e: any) => handleChange("SODIUM_LAB", e.target.value)}
                />
              </Box>
            </Grid>
          </Grid>

          {/* SYSTOLIC BLOOD PRESSURE */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.systolicbloodpressure")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("SYSTOLIC_BLOOD_PRESSURE") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("SYSTOLIC_BLOOD_PRESSURE", val);
                  }
                }}
                value={selectedMCPs["SYSTOLIC_BLOOD_PRESSURE"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("SYSTOLIC_BLOOD_PRESSURE") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* PAOFIO2 */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.PaOFIO2")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("PAO2_FIO2") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("PAO2_FIO2", val);
                  }
                }}
                value={selectedMCPs["PAO2_FIO2"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("PAO2_FIO2") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* WBC | RESULT */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {t("saps2-score.WBC")}
                <Box sx={{ display: "flex", alignItems: "center", width: "150px" }}>
                  <span style={{ fontSize: "30px", fontWeight: "100", marginRight: "8px" }}>|</span>
                  {t("saps2-score.result")}
                </Box>
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <ByzMcParameterDetailSelect
                  title={t("gbselect")}
                  mcpId={getMCPId("WBC") || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("WBC", val);
                    }
                  }}
                  value={selectedMCPs["WBC"]}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(getMCPId("WBC") || 0)}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
                <ByzMuiTextField
                  fullWidth
                  placeholder=""
                  disabled
                  value={typedFormData.WBC_LAB || ""}
                  onChange={(e: any) => handleChange("WBC_LAB", e.target.value)}
                />
              </Box>
            </Grid>
          </Grid>

          {/* HCO3 | RESULT */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {t("saps2-score.HCO3")}
                <Box sx={{ display: "flex", alignItems: "center", width: "150px" }}>
                  <span style={{ fontSize: "30px", fontWeight: "100", marginRight: "8px" }}>|</span>
                  {t("saps2-score.result")}
                </Box>
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <ByzMcParameterDetailSelect
                  title={t("gbselect")}
                  mcpId={getMCPId("HCO3") || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("HCO3", val);
                    }
                  }}
                  value={selectedMCPs["HCO3"]}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(getMCPId("HCO3") || 0)}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
                <ByzMuiTextField
                  fullWidth
                  placeholder=""
                  disabled
                  value={typedFormData.HCO3_LAB || ""}
                  onChange={(e: any) => handleChange("HCO3_LAB", e.target.value)}
                />
              </Box>
            </Grid>
          </Grid>

          {/* POTASIUM | RESULT */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {t("saps2-score.potasium")}
                <Box sx={{ display: "flex", alignItems: "center", width: "150px" }}>
                  <span style={{ fontSize: "30px", fontWeight: "100", marginRight: "8px" }}>|</span>
                  {t("saps2-score.result")}
                </Box>
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <ByzMcParameterDetailSelect
                  title={t("gbselect")}
                  mcpId={getMCPId("POTASSIUM") || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("POTASSIUM", val);
                    }
                  }}
                  value={selectedMCPs["POTASSIUM"]}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(getMCPId("POTASSIUM") || 0)}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
                <ByzMuiTextField
                  fullWidth
                  placeholder=""
                  disabled
                  value={typedFormData.POTASSIUM_LAB || ""}
                  onChange={(e: any) => handleChange("POTASSIUM_LAB", e.target.value)}
                />
              </Box>
            </Grid>
          </Grid>

          {/* BILIRUBIN | RESULT */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {t("saps2-score.bilirubin")}
                <Box sx={{ display: "flex", alignItems: "center", width: "150px" }}>
                  <span style={{ fontSize: "30px", fontWeight: "100", marginRight: "8px" }}>|</span>
                  {t("saps2-score.result")}
                </Box>
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <ByzMcParameterDetailSelect
                  title={t("gbselect")}
                  mcpId={getMCPId("BILIRUBIN") || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("BILIRUBIN", val);
                    }
                  }}
                  value={selectedMCPs["BILIRUBIN"]}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(getMCPId("BILIRUBIN") || 0)}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
                <ByzMuiTextField
                  fullWidth
                  placeholder=""
                  disabled
                  value={typedFormData.BILIRUBIN_LAB || ""}
                  onChange={(e: any) => handleChange("BILIRUBIN_LAB", e.target.value)}
                />
              </Box>
            </Grid>
          </Grid>

          {/* URINE OUTPUT */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.urineOutput")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("URINE_OUTPUT") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("URINE_OUTPUT", val);
                  }
                }}
                value={selectedMCPs["URINE_OUTPUT"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("URINE_OUTPUT") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* GLASKOW SCORE | RANGE */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {t("saps2-score.glaskowScore")}
                <Box sx={{ display: "flex", alignItems: "center", width: "150px" }}>
                  <span style={{ fontSize: "30px", fontWeight: "100", marginRight: "8px" }}>|</span>
                  {t("saps2-score.scoreRange")}
                </Box>
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <ByzMuiTextField
                  label={t("saps2-score.score")}
                  disabled
                  fullWidth
                  value={glasgowScoreValue !== null ? glasgowScoreValue.toString() : ""}
                />
                <ByzMuiTextField
                  label={t("saps2-score.point")}
                  disabled
                  fullWidth
                  value={selectedMCPs["GLASGOW_SCORE_SELECT"]?.name || ""}
                />
              </Box>
            </Grid>
          </Grid>

          {/* SAPS II SCORE */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.sapsIIScore")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMuiTextField
                label={t("saps2-score.sapsIIScore")}
                disabled
                fullWidth
                value={typedFormData.SAPSII_SCORE || ""}
              />
            </Grid>
          </Grid>

          {/* SAPS II EXTENDED TITLE */}
          <Grid
            md={6}
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <ByzMuiTypography isTitle>
              {t("saps2-score.sapsIIExtended")}
            </ByzMuiTypography>
          </Grid>

          {/* AGE (EXTENDED) */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.age")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("SE_AGE") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("SE_AGE", val);
                  }
                }}
                value={selectedMCPs["SE_AGE"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("SE_AGE") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* GENDER */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.gender")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              {selectedMCPs["GENDER"] ? (
                <ByzMuiTextField
                  label={t("gbselect")}
                  disabled
                  fullWidth
                  value={selectedMCPs["GENDER"]?.name || ""}
                />
              ) : (
                <ByzMcParameterDetailSelect
                  title={t("gbselect")}
                  mcpId={getMCPId("GENDER") || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("GENDER", val);
                    }
                  }}
                  value={selectedMCPs["GENDER"]}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(getMCPId("GENDER") || 0)}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              )}
            </Grid>
          </Grid>

          {/* BEFORE INTENSIVE CARE */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.beforeIntensiveCare")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("PREVIOUS_LOCATION") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("PREVIOUS_LOCATION", val);
                  }
                }}
                value={selectedMCPs["PREVIOUS_LOCATION"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("PREVIOUS_LOCATION") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* BEFORE INSENTIVE CARE WAIT TIME */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.beforeInsentiveCareWaitTime")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("DURATION_OF_STAY") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("DURATION_OF_STAY", val);
                  }
                }}
                value={selectedMCPs["DURATION_OF_STAY"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("DURATION_OF_STAY") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* CLINICAL CATEGORY */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.clinicalCategory")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("CLINICAL_CATEGORY") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("CLINICAL_CATEGORY", val);
                  }
                }}
                value={selectedMCPs["CLINICAL_CATEGORY"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("CLINICAL_CATEGORY") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* POISONING */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.poisoning")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMcParameterDetailSelect
                title={t("gbselect")}
                mcpId={getMCPId("POISONING") || 0}
                multiple={false}
                handleChange={(val) => {
                  if (!Array.isArray(val)) {
                    handleMCPChange("POISONING", val);
                  }
                }}
                value={selectedMCPs["POISONING"]}
                sx={{ width: "100%" }}
                cachedData={getMCPParameterDetails(getMCPId("POISONING") || 0)}
                cacheLoaded={mcpCacheLoaded}
                readOnly={!extraAccess}
              />
            </Grid>
          </Grid>

          {/* SAPS II EXTENDED SCORE */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.sapsIIExtendedScore")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMuiTextField
                label={t("saps2-score.sapsIIExtendedScore")}
                placeholder=""
                disabled
                fullWidth
                value={typedFormData.SAPSII_EXTENDED_SCORE || ""}
              />
            </Grid>
          </Grid>

          {/* EXPECTED MORTALITY */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("saps2-score.expectedMortality")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMuiTextField
                label={t("saps2-score.expectedMortality")}
                placeholder={t("saps2-score.calculated-after-save")}
                disabled
                fullWidth
                value={typedFormData.EXPECTED_MORTALITY || ""}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SapsIIScoreCreate;
